import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AIBookingAssistant from "../components/AIBookingAssistant";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Search, ArrowRight, MapPin, Sparkles, Home, Wrench, Trees, Truck,
  Paintbrush, Zap, Car, Scissors, Star, Shield, Clock, CheckCircle2, Bot, MessageSquare,
  ShieldCheck, ScanFace, BadgeCheck, CreditCard, Fingerprint, FileCheck
} from "lucide-react";

// 88 Service Categories for search autocomplete
const SERVICE_CATEGORIES = [
  "House Cleaning", "Deep Cleaning", "Move-In/Move-Out Cleaning", "Office Cleaning", 
  "Carpet Cleaning", "Window Cleaning", "Pressure Washing", "Gutter Cleaning", 
  "Pool Cleaning", "Chimney Cleaning", "Air Duct Cleaning", "Upholstery Cleaning", 
  "Tile & Grout Cleaning", "Post-Construction Cleaning", "Garage Cleaning",
  "Lawn Mowing", "Landscaping", "Tree Trimming", "Hedge Trimming", "Garden Design", 
  "Leaf Removal", "Snow Removal", "Irrigation System Install/Repair", "Mulching", "Sod Installation",
  "Plumbing Repair", "Drain Cleaning", "Water Heater Install/Repair", "Pipe Replacement", 
  "Toilet Repair", "Faucet Install", "Sump Pump Service",
  "Electrical Repair", "Outlet/Switch Install", "Ceiling Fan Install", "Lighting Install", 
  "Panel Upgrade", "Generator Install", "EV Charger Install",
  "HVAC Repair", "AC Install", "Furnace Repair", "Duct Cleaning", "Thermostat Install", "Heat Pump Service",
  "Interior Painting", "Exterior Painting", "Cabinet Painting", "Deck Staining", "Wallpaper Install/Removal",
  "Drywall Repair", "Roof Repair", "Roof Replacement", "Gutter Install/Repair", "Siding Repair", 
  "Deck Building", "Fence Install/Repair", "Patio/Paver Install", "Concrete Work", "Foundation Repair",
  "Kitchen Remodel", "Bathroom Remodel", "Basement Finishing", "Flooring Install", "Tile Install", 
  "Countertop Install", "Cabinet Install",
  "Appliance Repair", "Appliance Install", "Pest Control", "Termite Treatment", "Wildlife Removal", 
  "Bed Bug Treatment",
  "Locksmith", "Security System Install", "Camera Install", "Smart Home Setup",
  "Junk Removal", "Dumpster Rental", "Furniture Assembly", "Handyman Services",
  "Home Inspection", "Mold Remediation", "Radon Testing", "Septic Service", "Well Service", 
  "Solar Panel Install", "Window Install/Replacement"
];

// Guide card images from design guidelines
const GUIDE_IMAGES = {
  cost: "https://images.pexels.com/photos/30139495/pexels-photo-30139495.jpeg",
  maintenance: "https://images.unsplash.com/photo-1567361808960-dec9cb578182?w=800&h=600&fit=crop",
  projects: "https://images.unsplash.com/photo-1770763233593-74dfd0da7bf0?w=800&h=600&fit=crop"
};

// Service card images - Professional quality
const SERVICE_IMAGES = {
  cleaning: "https://images.pexels.com/photos/6196239/pexels-photo-6196239.jpeg?w=400&h=400&fit=crop",
  movers: "https://images.pexels.com/photos/7464703/pexels-photo-7464703.jpeg?w=400&h=400&fit=crop",
  pressure: "https://images.unsplash.com/photo-1707897283727-31befe824066?w=400&h=400&fit=crop",
  landscaping: "https://images.pexels.com/photos/4920283/pexels-photo-4920283.jpeg?w=400&h=400&fit=crop",
  plumbing: "https://images.pexels.com/photos/32588548/pexels-photo-32588548.jpeg?w=400&h=400&fit=crop",
  electrical: "https://images.pexels.com/photos/34054464/pexels-photo-34054464.jpeg?w=400&h=400&fit=crop",
  handyman: "https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?w=400&h=400&fit=crop",
  painting: "https://images.pexels.com/photos/5493654/pexels-photo-5493654.jpeg?w=400&h=400&fit=crop",
  mechanic: "https://customer-assets.emergentagent.com/job_pro-gigs-world/artifacts/vvqv8kj9_image.png",
  auto_detailing: "https://customer-assets.emergentagent.com/job_pro-gigs-world/artifacts/ldkxkuyi_image.png"
};

// New Jersey cities for quick selection
const popularCities = [
  "Newark", "Jersey City", "Paterson", "Elizabeth", "Edison",
  "Woodbridge", "Lakewood", "Toms River", "Hamilton", "Trenton",
  "Clifton", "Camden", "Brick", "Cherry Hill", "Passaic",
  "Union City", "Old Bridge", "Middletown", "East Orange", "Bayonne"
];

const categoryTabs = [
  { id: "cleaning", name: "Cleaners", icon: Sparkles },
  { id: "handyman", name: "Handymen", icon: Wrench },
  { id: "gardening", name: "Landscapers", icon: Trees },
  { id: "moving", name: "Movers", icon: Truck },
  { id: "painting", name: "Painters", icon: Paintbrush },
  { id: "electrical", name: "Electricians", icon: Zap },
  { id: "mechanic", name: "Mechanics", icon: Car },
  { id: "grooming", name: "Groomers", icon: Scissors }
];

const serviceCards = [
  { id: "cleaning", name: "House Cleaning", image: SERVICE_IMAGES.cleaning, category: "cleaning" },
  { id: "movers", name: "Moving Help", image: SERVICE_IMAGES.movers, category: "moving" },
  { id: "pressure", name: "Pressure Washing", image: SERVICE_IMAGES.pressure, category: "cleaning" },
  { id: "landscaping", name: "Lawn Care", image: SERVICE_IMAGES.landscaping, category: "gardening" },
  { id: "plumbing", name: "Plumbing", image: SERVICE_IMAGES.plumbing, category: "plumbing" },
  { id: "electrical", name: "Electrical", image: SERVICE_IMAGES.electrical, category: "electrical" },
  { id: "handyman", name: "Handyman", image: SERVICE_IMAGES.handyman, category: "handyman" },
  { id: "painting", name: "Painting", image: SERVICE_IMAGES.painting, category: "painting" },
  { id: "mechanic", name: "Auto Mechanic", image: SERVICE_IMAGES.mechanic, category: "mechanic" },
  { id: "auto_detailing", name: "Auto Detailing", image: SERVICE_IMAGES.auto_detailing, category: "mechanic" }
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("Newark");
  const [activeCategory, setActiveCategory] = useState("cleaning");
  const [categories, setCategories] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Filter suggestions based on search query
  useEffect(() => {
    if (searchQuery.length > 0) {
      const filtered = SERVICE_CATEGORIES.filter(category =>
        category.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    navigate(`/post-job?service=${encodeURIComponent(suggestion)}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (searchQuery.trim()) {
      navigate(`/post-job?service=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate("/professionals");
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/professionals?category=${categoryId}`);
  };

  const filteredServices = serviceCards.filter(
    service => service.category === activeCategory || activeCategory === "all"
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-navy via-navy-dark to-navy py-16 md:py-24 px-4 overflow-hidden" data-testid="hero-section">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
          </div>
          
          <div className="max-w-5xl mx-auto relative">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                Connect with Verified Local Experts
              </h1>
              <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                New Jersey&apos;s trusted marketplace for quality home services. Every professional is background-checked and verified.
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
              <div className="flex items-center bg-white rounded-lg shadow-2xl overflow-hidden">
                <Search className="w-5 h-5 text-slate-400 ml-5 flex-shrink-0" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="What service do you need? (e.g., Plumbing, Cleaning)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length > 0 && setShowSuggestions(true)}
                  className="flex-1 border-0 text-base py-5 px-4 focus-visible:ring-0 placeholder:text-slate-400"
                  data-testid="search-input"
                  autoComplete="off"
                />
                <Button 
                  type="submit" 
                  className="bg-gold hover:bg-gold-dark text-navy font-semibold rounded-lg m-1.5 px-6 h-11"
                  data-testid="search-btn"
                >
                  Search
                </Button>
              </div>
              
              {/* Search Autocomplete Dropdown */}
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div 
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden z-50"
                  data-testid="search-suggestions"
                >
                  <div className="p-2">
                    <p className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Suggested Services
                    </p>
                    {filteredSuggestions.map((suggestion, index) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3 group"
                        data-testid={`suggestion-${index}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center group-hover:bg-navy/20 transition-colors">
                          <Search className="w-4 h-4 text-navy" />
                        </div>
                        <span className="text-slate-700 font-medium">{suggestion}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </form>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-12 mt-10">
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-gold">500+</p>
                <p className="text-white/70 text-sm">Verified Pros</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-gold">88</p>
                <p className="text-white/70 text-sm">Service Categories</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-gold">21</p>
                <p className="text-white/70 text-sm">NJ Counties</p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals - Our Pros are Verified */}
        <section className="py-16 px-4 bg-slate-50" data-testid="verified-pros-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-navy/10 text-navy rounded-full text-sm font-semibold mb-4">
                <ShieldCheck className="w-4 h-4" />
                Verified Professionals
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Our Pros are Verified
              </h2>
              <p className="text-slate text-lg max-w-2xl mx-auto">
                Every professional on BQL goes through a rigorous verification process. Hire with confidence knowing you&apos;re working with trusted experts.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Government ID Verified */}
              <div className="bg-white rounded-xl p-8 shadow-professional text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-navy to-navy-light flex items-center justify-center mx-auto mb-6">
                  <FileCheck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Government ID Verified</h3>
                <p className="text-slate">
                  Every professional submits a valid government-issued ID that is verified against official databases.
                </p>
              </div>

              {/* Face Scan Verified */}
              <div className="bg-white rounded-xl p-8 shadow-professional text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold to-gold-light flex items-center justify-center mx-auto mb-6">
                  <ScanFace className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Face Scan Verified</h3>
                <p className="text-slate">
                  AI-powered liveness detection ensures the person matches their ID with advanced biometric checks.
                </p>
              </div>

              {/* Licensed & Insured */}
              <div className="bg-white rounded-xl p-8 shadow-professional text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-6">
                  <BadgeCheck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">Licensed & Insured</h3>
                <p className="text-slate">
                  We verify professional credentials, licenses, and insurance for applicable service categories.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 px-4 bg-white" data-testid="how-it-works">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                How BQL Works
              </h2>
              <p className="text-slate text-lg">
                Getting connected with the right professional is simple
              </p>
            </div>
            
            {/* For Customers */}
            <div className="mb-16">
              <h3 className="text-xl font-bold text-navy mb-8 text-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full">
                  For Customers — 100% FREE
                </span>
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center text-lg font-bold">1</div>
                  <div className="bg-slate-50 rounded-xl p-6 pl-10 ml-4">
                    <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center mb-4">
                      <MessageSquare className="w-6 h-6 text-navy" />
                    </div>
                    <h4 className="text-lg font-bold text-navy mb-2">Describe Your Job</h4>
                    <p className="text-slate text-sm">
                      Tell us what you need in plain English or any language. Our AI understands and categorizes your request.
                    </p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center text-lg font-bold">2</div>
                  <div className="bg-slate-50 rounded-xl p-6 pl-10 ml-4">
                    <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center mb-4">
                      <ShieldCheck className="w-6 h-6 text-navy" />
                    </div>
                    <h4 className="text-lg font-bold text-navy mb-2">Get Matched</h4>
                    <p className="text-slate text-sm">
                      Verified pros in your area respond with competitive bids. Compare reviews, ratings, and credentials.
                    </p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center text-lg font-bold">3</div>
                  <div className="bg-slate-50 rounded-xl p-6 pl-10 ml-4">
                    <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-6 h-6 text-navy" />
                    </div>
                    <h4 className="text-lg font-bold text-navy mb-2">Hire & Relax</h4>
                    <p className="text-slate text-sm">
                      Pick your pro, track progress, and pay securely. Leave a review to help other customers.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* For Professionals */}
            <div>
              <h3 className="text-xl font-bold text-navy mb-8 text-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 text-gold-dark rounded-full">
                  For Professionals — Grow Your Business
                </span>
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-10 h-10 rounded-full bg-gold text-navy flex items-center justify-center text-lg font-bold">1</div>
                  <div className="bg-slate-50 rounded-xl p-6 pl-10 ml-4">
                    <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4">
                      <Fingerprint className="w-6 h-6 text-gold-dark" />
                    </div>
                    <h4 className="text-lg font-bold text-navy mb-2">Register & Get Verified</h4>
                    <p className="text-slate text-sm">
                      Complete ID verification in minutes. Build trust with customers instantly with your verified badge.
                    </p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-10 h-10 rounded-full bg-gold text-navy flex items-center justify-center text-lg font-bold">2</div>
                  <div className="bg-slate-50 rounded-xl p-6 pl-10 ml-4">
                    <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4">
                      <Sparkles className="w-6 h-6 text-gold-dark" />
                    </div>
                    <h4 className="text-lg font-bold text-navy mb-2">Receive Job Alerts</h4>
                    <p className="text-slate text-sm">
                      Get notified instantly when customers in your area post jobs matching your services.
                    </p>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-10 h-10 rounded-full bg-gold text-navy flex items-center justify-center text-lg font-bold">3</div>
                  <div className="bg-slate-50 rounded-xl p-6 pl-10 ml-4">
                    <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4">
                      <Zap className="w-6 h-6 text-gold-dark" />
                    </div>
                    <h4 className="text-lg font-bold text-navy mb-2">Get Paid Fast</h4>
                    <p className="text-slate text-sm">
                      Win jobs and get paid directly. With OnePay integration, receive instant payouts to your account.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI Booking Assistant Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-navy via-navy-dark to-navy-light" data-testid="ai-assistant-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
                <Bot className="w-4 h-4" />
                <span>AI-Powered Booking</span>
                <Sparkles className="w-4 h-4" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Tell us what you need — we&apos;ll handle the rest
              </h2>
              <p className="text-white/80 max-w-xl mx-auto text-lg">
                Our AI assistant understands any language. Just talk or type and get connected with the right pros instantly.
              </p>
              <div className="flex items-center justify-center gap-3 mt-4 text-white/60 text-sm">
                <span>English</span>
                <span>•</span>
                <span>Español</span>
                <span>•</span>
                <span>中文</span>
                <span>•</span>
                <span>Français</span>
                <span>•</span>
                <span>+ more</span>
              </div>
            </div>

            {showAIAssistant ? (
              <AIBookingAssistant onClose={() => setShowAIAssistant(false)} />
            ) : (
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg mx-auto">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-navy to-navy-light flex items-center justify-center">
                    <MessageSquare className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-navy text-lg">BQL Concierge</h3>
                    <p className="text-slate text-sm">Get connected in seconds</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-slate font-medium">Try saying:</p>
                  <div className="flex flex-wrap gap-2">
                    {["I need a plumber in Newark", "Deep cleaning for my house", "Tree trimming in Hoboken"].map((example, i) => (
                      <button
                        key={i}
                        onClick={() => setShowAIAssistant(true)}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors border border-slate-200"
                      >
                        &ldquo;{example}&rdquo;
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => setShowAIAssistant(true)}
                  className="w-full bg-gradient-to-r from-navy to-navy-light hover:from-navy-dark hover:to-navy text-white rounded-lg py-6 text-lg font-semibold shadow-lg"
                  data-testid="open-ai-assistant-btn"
                >
                  <Bot className="w-5 h-5 mr-2" />
                  Start Chatting with AI
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Pros for Every Project Section */}
        <section className="py-16 px-4 bg-white" data-testid="category-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Pros for Every Project
              </h2>
              <p className="text-slate text-lg">
                From home repairs to specialized services, find the expert you need
              </p>
            </div>
            
            {/* Category Tabs */}
            <div className="mb-8 overflow-x-auto scrollbar-hide" data-testid="category-tabs">
              <div className="flex gap-2 justify-center flex-wrap">
                {categoryTabs.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                        activeCategory === cat.id
                          ? "bg-navy text-white shadow-lg"
                          : "bg-slate-100 text-slate hover:bg-slate-200"
                      }`}
                      data-testid={`category-tab-${cat.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Service Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="service-grid">
              {filteredServices.length > 0 
                ? filteredServices.slice(0, 8).map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleCategoryClick(service.category)}
                    className="group relative rounded-xl overflow-hidden aspect-square shadow-professional hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1"
                    data-testid={`service-card-${service.id}`}
                  >
                    <img 
                      src={service.image} 
                      alt={service.name} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-bold text-lg">{service.name}</h3>
                    </div>
                  </button>
                ))
                : serviceCards.slice(0, 8).map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleCategoryClick(service.category)}
                    className="group relative rounded-xl overflow-hidden aspect-square shadow-professional hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1"
                    data-testid={`service-card-${service.id}`}
                  >
                    <img 
                      src={service.image} 
                      alt={service.name} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-bold text-lg">{service.name}</h3>
                    </div>
                  </button>
                ))
              }
            </div>

            {/* View All Button */}
            <div className="text-center mt-10">
              <Link to="/categories">
                <Button 
                  variant="outline" 
                  className="rounded-lg px-8 border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold"
                  data-testid="view-all-services-btn"
                >
                  View All 88 Services
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* New Jersey Locations Section */}
        <section className="py-16 px-4 bg-slate-50" data-testid="nj-locations-section">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
              Serving All of New Jersey
            </h2>
            <p className="text-slate text-lg mb-8">
              From Newark to Cape May, find trusted professionals in your area
            </p>
            
            {/* Cities Grid */}
            <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="city-pills">
              {popularCities.slice(0, 15).map((city) => (
                <button
                  key={city}
                  onClick={() => setSelectedCity(city)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedCity === city
                      ? "bg-navy text-white shadow-md"
                      : "bg-white border border-slate-200 text-slate hover:border-navy hover:text-navy"
                  }`}
                  data-testid={`city-pill-${city.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {city}
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => navigate("/categories")}
              className="text-navy font-semibold hover:underline inline-flex items-center gap-2"
              data-testid="find-town-btn"
            >
              <MapPin className="w-4 h-4" />
              Browse all NJ counties and cities
            </button>
          </div>
        </section>

        {/* Security Badges Section */}
        <section className="py-12 px-4 bg-white border-t border-slate-100" data-testid="security-badges">
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-slate text-sm font-medium mb-8">TRUSTED BY HOMEOWNERS ACROSS NEW JERSEY</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
              <div className="flex items-center gap-3 text-slate-600">
                <CreditCard className="w-8 h-8 text-[#635BFF]" />
                <div className="text-left">
                  <p className="font-semibold text-navy text-sm">Payments Secured</p>
                  <p className="text-xs text-slate">by Stripe</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Fingerprint className="w-8 h-8 text-[#3B5998]" />
                <div className="text-left">
                  <p className="font-semibold text-navy text-sm">Identity Verified</p>
                  <p className="text-xs text-slate">by Persona</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Zap className="w-8 h-8 text-emerald-500" />
                <div className="text-left">
                  <p className="font-semibold text-navy text-sm">Instant Pay</p>
                  <p className="text-xs text-slate">via OnePay</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-navy via-navy-dark to-navy" data-testid="cta-section">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Post your job for free and connect with verified professionals today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register/customer">
                <Button 
                  size="lg" 
                  className="bg-gold hover:bg-gold-dark text-navy font-bold rounded-lg px-8 w-full sm:w-auto shadow-lg"
                  data-testid="cta-post-job-btn"
                >
                  Post a Job for Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register/professional">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 rounded-lg px-8 w-full sm:w-auto font-semibold"
                  data-testid="cta-join-pro-btn"
                >
                  Join as a Professional
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
